import React, { useState } from "react";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatINR, formatApiError, useSEO } from "@/lib/api";

const field = "w-full bg-[#F5F5F5] rounded-full px-6 py-4 text-sm outline-none focus:ring-1 ring-black transition";

export default function OrderTracking() {
  useSEO("Track Order");
  const [f, setF] = useState({ order_number: "", email: "" });
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setOrder(null);
    try {
      const { data } = await api.get(`/orders/track?order_number=${encodeURIComponent(f.order_number)}&email=${encodeURIComponent(f.email)}`);
      setOrder(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="track-page" className="pt-32 pb-28">
      <div className="max-w-[900px] mx-auto px-6 md:px-10">
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">Order Tracking</p>
        <h1 className="font-display text-5xl md:text-7xl tracking-[-0.03em] mb-10">Track your order</h1>

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 mb-12" data-testid="track-form">
          <input data-testid="track-order-number" required placeholder="Order number (VYR-XXXXXXXX)" value={f.order_number}
            onChange={(e) => setF({ ...f, order_number: e.target.value })} className={field} />
          <input data-testid="track-email" type="email" required placeholder="Email used at checkout" value={f.email}
            onChange={(e) => setF({ ...f, email: e.target.value })} className={field} />
          <button type="submit" disabled={busy} data-testid="track-submit"
            className="bg-black text-white rounded-full px-8 py-4 uppercase text-xs tracking-[0.2em] disabled:opacity-60">
            {busy ? "…" : "Track"}
          </button>
        </form>

        {order && (
          <div className="border border-black/10 rounded-3xl p-8" data-testid="track-result">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <div>
                <p className="font-display text-2xl">{order.order_number}</p>
                <p className="text-sm text-muted-foreground">Placed {new Date(order.created_at).toLocaleDateString()}</p>
              </div>
              <span className={`text-xs uppercase tracking-widest px-4 py-1.5 rounded-full ${order.payment_status === "paid" ? "bg-black text-white" : "bg-[#F5F5F5]"}`}>
                {order.payment_status === "paid" ? "Paid" : order.payment_status}
              </span>
            </div>

            {/* Tracking steps */}
            <div className="relative flex justify-between mb-10">
              <div className="absolute top-3 left-0 right-0 h-px bg-black/10" />
              {order.tracking?.map((t, i) => (
                <div key={i} className="relative z-10 flex flex-col items-center text-center flex-1">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${t.done ? "bg-black text-white" : "bg-white border border-black/20"}`}>
                    {t.done && <Check size={13} />}
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.12em] mt-2 max-w-[70px]">{t.stage}</span>
                </div>
              ))}
            </div>

            <div className="border-t border-black/10 pt-6 space-y-3">
              {order.items.map((it) => (
                <div key={it.slug} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-3">
                    <img src={`${it.image}?w=80&q=70`} alt="" className="w-10 h-10 rounded-xl object-cover" />
                    {it.name} × {it.quantity}
                  </span>
                  <span>{formatINR(it.price * it.quantity)}</span>
                </div>
              ))}
              <div className="flex justify-between pt-4 border-t border-black/10 text-base">
                <span className="uppercase tracking-widest">Total</span>
                <span>{formatINR(order.amount)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
